import { useEffect, useRef, useState } from "react";

const AMBIENT_MAP = {
  day: "/stageAssets/stage1-plant/audio/forest-day.mp3",
  sunset: "/stageAssets/stage1-plant/audio/forest-transition.mp3",
  sunrise: "/stageAssets/stage1-plant/audio/forest-transition.mp3",
  night: "/stageAssets/stage1-plant/audio/forest-night.mp3",
};

const STORM_SRC = "/stageAssets/stage1-plant/audio/forest-storm.mp3";
const WIND_SRC = "/stageAssets/stage1-plant/audio/wind.mp3";

function fadeAudio(audio, targetVolume, duration = 2500, onComplete) {
  if (!audio) return;

  const steps = 40;
  const intervalTime = duration / steps;
  const startVolume = audio.volume;
  const change = targetVolume - startVolume;
  let step = 0;

  const interval = setInterval(() => {
    step += 1;

    const progress = step / steps;
    const nextVolume = startVolume + change * progress;

    audio.volume = Math.max(0, Math.min(1, nextVolume));

    if (step >= steps) {
      audio.volume = Math.max(0, Math.min(1, targetVolume));
      clearInterval(interval);

      if (onComplete) onComplete();
    }
  }, intervalTime);

  return interval;
}

export default function PlantAudio({
  activateEndTransition = false,
  mode = "day",
  isStorming = false,
  volume = 0.6,
  rainVolume = 0.28,
  windVolume = 0.4,
}) {
  const ambientARef = useRef(null);
  const ambientBRef = useRef(null);
  const activeAmbientRef = useRef("A");
  const currentModeRef = useRef("day");

  const stormRef = useRef(null);
  const windRef = useRef(null);

  const fadeIntervalsRef = useRef([]);
  const windLoopTimeoutRef = useRef(null);
  const prevEndTransitionRef = useRef(false);

  const [isUnlocked, setIsUnlocked] = useState(false);

  // Unlock audio after first interaction
  useEffect(() => {
    const unlock = () => setIsUnlocked(true);

    window.addEventListener("click", unlock, { once: true });
    window.addEventListener("touchstart", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });

    return () => {
      window.removeEventListener("click", unlock);
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  // Create audio objects once
  useEffect(() => {
    ambientARef.current = new Audio(AMBIENT_MAP.day);
    ambientARef.current.loop = true;
    ambientARef.current.preload = "auto";
    ambientARef.current.volume = 0;

    ambientBRef.current = new Audio(AMBIENT_MAP.day);
    ambientBRef.current.loop = true;
    ambientBRef.current.preload = "auto";
    ambientBRef.current.volume = 0;

    stormRef.current = new Audio(STORM_SRC);
    stormRef.current.loop = true;
    stormRef.current.preload = "auto";
    stormRef.current.volume = 0;

    windRef.current = new Audio(WIND_SRC);
    windRef.current.loop = false;
    windRef.current.preload = "auto";
    windRef.current.volume = windVolume;

    return () => {
      fadeIntervalsRef.current.forEach(clearInterval);

      if (windLoopTimeoutRef.current) {
        clearTimeout(windLoopTimeoutRef.current);
      }

      [ambientARef, ambientBRef, stormRef, windRef].forEach((ref) => {
        if (ref.current) {
          ref.current.pause();
          ref.current.onended = null;
          ref.current.src = "";
          ref.current = null;
        }
      });
    };
  }, []);

  // Start first ambient track after unlock
  useEffect(() => {
    if (!isUnlocked) return;

    const ambient = ambientARef.current;
    if (!ambient) return;

    ambient.src = AMBIENT_MAP[mode] || AMBIENT_MAP.day;
    ambient.loop = true;
    ambient.volume = 0;

    ambient.play().catch((err) => {
      console.warn("Ambient playback blocked:", err);
    });

    fadeIntervalsRef.current.push(
      fadeAudio(ambient, volume, 2500)
    );

    currentModeRef.current = mode;
  }, [isUnlocked]);

  // Crossfade ambient tracks between day/night/sunset/sunrise
  useEffect(() => {
    if (!isUnlocked) return;
    if (currentModeRef.current === mode) return;

    const nextSrc = AMBIENT_MAP[mode] || AMBIENT_MAP.day;

    const currentAmbient =
      activeAmbientRef.current === "A"
        ? ambientARef.current
        : ambientBRef.current;

    const nextAmbient =
      activeAmbientRef.current === "A"
        ? ambientBRef.current
        : ambientARef.current;

    if (!currentAmbient || !nextAmbient) return;

    nextAmbient.pause();
    nextAmbient.src = nextSrc;
    nextAmbient.load();
    nextAmbient.loop = true;
    nextAmbient.volume = 0;

    nextAmbient.play().catch((err) => {
      console.warn("Next ambient playback blocked:", err);
    });

    fadeIntervalsRef.current.push(
      fadeAudio(currentAmbient, 0, 3500, () => {
        currentAmbient.pause();
        currentAmbient.currentTime = 0;
      })
    );

    fadeIntervalsRef.current.push(
      fadeAudio(nextAmbient, volume, 3500)
    );

    activeAmbientRef.current =
      activeAmbientRef.current === "A" ? "B" : "A";

    currentModeRef.current = mode;
  }, [mode, isUnlocked, volume]);

  // Rain / storm audio
  // Keep the loop alive and only fade volume
  useEffect(() => {
    if (!isUnlocked) return;

    const storm = stormRef.current;
    if (!storm) return;

    if (isStorming) {
      storm.loop = true;

      if (storm.paused) {
        storm.volume = 0;

        storm.play().catch((err) => {
          console.warn("Storm playback blocked:", err);
        });
      }

      fadeIntervalsRef.current.push(
        fadeAudio(storm, rainVolume, 3500)
      );
    } else {
      // longer fade out so rain lingers naturally
      fadeIntervalsRef.current.push(
        fadeAudio(storm, 0, 6000, () => {
          storm.pause();
          // intentionally do NOT reset currentTime
        })
      );
    }
  }, [isStorming, isUnlocked, rainVolume]);

  // End transition wind loop
  useEffect(() => {
    if (!isUnlocked) return;

    const wasActive = prevEndTransitionRef.current;
    const wind = windRef.current;

    if (!wind) return;

    const playWindWithPause = () => {
      wind.currentTime = 0;
      wind.volume = 0;

      wind.play().catch((err) => {
        console.warn("Wind playback blocked:", err);
      });

      fadeIntervalsRef.current.push(
        fadeAudio(wind, windVolume, 1000)
      );

      wind.onended = () => {
        windLoopTimeoutRef.current = setTimeout(() => {
          playWindWithPause();
        }, 1500);
      };
    };

    if (activateEndTransition && !wasActive) {
      playWindWithPause();
    }

    if (!activateEndTransition && wasActive) {
      if (windLoopTimeoutRef.current) {
        clearTimeout(windLoopTimeoutRef.current);
      }

      fadeIntervalsRef.current.push(
        fadeAudio(wind, 0, 1800, () => {
          wind.pause();
          wind.currentTime = 0;
          wind.onended = null;
        })
      );
    }

    prevEndTransitionRef.current = activateEndTransition;
  }, [activateEndTransition, isUnlocked, windVolume]);

  return null;
}